import { useState } from 'react';
import { Layout, Menu, theme, Avatar, Space, Button, Dropdown } from 'antd';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import {
  HomeOutlined,
  TeamOutlined,
  MessageOutlined,
  SettingOutlined,
  DatabaseOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HistoryOutlined,
  BarChartOutlined,
  LogoutOutlined,
  ProfileOutlined,
  BookOutlined
} from '@ant-design/icons';
import ConversationList from './components/ConversationList';
import UserList from './components/UserList';
import CTAList from './components/CTAList';
import ConversationDetail from "./components/ConversationDetail.jsx";
import AuthRoute from "./components/AuthRoute.jsx";
import ErrorPage from './components/ErrorPage';
import { logout } from "./services/auth.js";
import FeedbackManagement from './components/FeedbackManagement';
import { useGlobalState } from './hooks/useGlobalState.js'
import PersonalCenter from './components/PersonalCenter';
import ModelList from "./components/ModelList.jsx";
import Dashboard from './components/Dashboard.jsx';
import Login from './components/Login.jsx';
import ApiDocumentCenter from './components/ApiDocumentCenter.jsx';
import apiDocIcon from './assets/API接口.png';
const { Header, Sider, Content } = Layout;

function MainLayout() {
  const { userProfile } = useGlobalState();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isPersonalCenterOpen, setPersonalCenterOpen] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const isCompanyUser = userProfile?.user?.userStatus === "Company User";
  const currentHostname = window.location.hostname;
  const isSystemSite = currentHostname === 'admin-dev.scams.report' || currentHostname === 'admin.scams.report';
  const apiDocumentationPath = isSystemSite
    ? '/docs/scam-report-enterprise/api/'
    : '/docs/scam-report/api/';

  const handleLogout = async () => {
    await logout();
    navigate('/error?error=logout');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Personal information',
    },
    // {
    //   key: 'settings',
    //   icon: <SettingOutlined />,
    //   label: 'Setting',
    // },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign out',
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'profile') {
      setPersonalCenterOpen(true);
    }
  };

  const handleOpenApiDocumentation = () => {
    window.open(apiDocumentationPath, '_blank', 'noopener,noreferrer');
  };

  const renderContent = () => {
    return (<Routes>
      <Route path="/home" element={<Dashboard />} />
      <Route path="/cta" element={<CTAList />} />
      <Route path="/users" element={<UserList />} />
      <Route path="/conversations" element={<ConversationList />} />
      <Route path="/documentation" element={<ApiDocumentCenter />} />
      {!isCompanyUser && <Route path="/feedbackManagement" element={<FeedbackManagement />} />}
      <Route path="/conversations/:id" element={<ConversationDetail />} />
      {!isCompanyUser && <Route path="/system/models" element={<ModelList />} />}
      {!isCompanyUser && <Route path="/system/logs" element={<div>Login Logs (In Development)</div>} />}
    </Routes>);
  };

  const wrapMenuItemWithLink = (item) => {
    if (item.children) {
      return {
        ...item,
        children: item.children.map(wrapMenuItemWithLink)
      };
    }

    return {
      ...item,
      label: (
        <a
          href={item.key}
          onClick={(e) => {
            e.preventDefault();
            navigate(item.key);
          }}
          style={{
            color: 'inherit',
            textDecoration: 'none',
            display: 'block',
            width: '100%'
          }}
        >
          {item.originalLabel || item.label}
        </a>
      ),
      originalLabel: item.label
    };
  };

  const menuItems = [
    {
      key: '/home',
      icon: <HomeOutlined />,
      label: 'Home',
    },
    {
      key: '/cta',
      icon: <BarChartOutlined />,
      label: 'CTA List',
    },
    {
      key: '/users',
      icon: <TeamOutlined />,
      label: 'User Management',
    },
    {
      key: '/conversations',
      icon: <MessageOutlined />,
      label: 'Conversation Management',
    },
    ...(!isCompanyUser ? [{
      key: '/feedbackManagement',
      icon: <ProfileOutlined />,
      label: 'User Feedback',
    }] : []),
    ...(!isCompanyUser ? [{
      key: 'system',
      icon: <SettingOutlined />,
      label: 'System Settings',
      children: [
        {
          key: '/system/models',
          icon: <DatabaseOutlined />,
          label: 'Model Management',
        },
        {
          key: '/system/logs',
          icon: <HistoryOutlined />,
          label: 'Login Logs',
        }, {
          key: '/documentation',
          icon: <BookOutlined />,
          label: 'API Documentation',
        }
      ],
    }] : []),
  ].map(wrapMenuItemWithLink);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={200}
        theme="dark"
        collapsible
        collapsed={collapsed}
        trigger={null}
        style={{
          position: 'fixed',
          height: '100vh',
          zIndex: 1,
          left: 0,
          top: 0,
          overflow: 'auto'
        }}
      >
        <div style={{
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          whiteSpace: 'nowrap',
          overflow: 'hidden'
        }}>
          <img
            src="/logo.png"
            alt="Scams Report Logo"
            style={{ width: '32px', height: '32px', marginRight: collapsed ? 0 : '12px' }}
          />
          {!collapsed && ((currentHostname === 'admin-dev.scams.report' || currentHostname === 'admin.scams.report') ? (
            <h1 style={{ color: '#fff', margin: 0, fontSize: '18px', lineHeight: '32px' }}>Scams Report</h1>
          ) : (
            <h1
              style={{
                color: '#fff',
                margin: 0,
                fontSize: '17px',
                lineHeight: '18px',
                textAlign: 'center',
              }}
            >
              Scams Report
              <br />
              <span
                style={{
                  fontSize: '17px',
                  lineHeight: '18px',
                  display: 'block',
                }}
              >
                Enterprise
              </span>
            </h1>
          ))}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          onClick={({ key }) => {
            if (key !== 'system') {
              return;
            }
          }}
          items={menuItems}
        />
      </Sider>
      <Layout style={{
        marginLeft: collapsed ? 80 : 200,
        minHeight: '100vh',
        position: 'relative'
      }}>
        <Header style={{
          background: colorBgContainer,
          padding: '0 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              height: 64,
            }}
          >
            {isCompanyUser && (
              <Button
                type="text"
                onClick={handleOpenApiDocumentation}
                style={{
                  width: 40,
                  height: 40,
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
                title="API Documentation"
              >
                <img
                  src={apiDocIcon}
                  alt="API Documentation"
                  style={{ width: 40, height: 40, objectFit: 'contain', display: 'block' }}
                />
              </Button>)}
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              trigger={['click']}
            >
              <div
                className="cursor-pointer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  height: 40,
                }}
              >
                <Avatar
                  src={userProfile?.user?.avatar ? `${userProfile?.user?.avatar}?t=${new Date().getTime()}` : undefined}
                  icon={<UserOutlined />}
                />
                <span style={{ lineHeight: '32px' }}>{userProfile?.user?.nickName}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: '16px', overflow: 'auto' }}>
          <div style={{ padding: 24, background: colorBgContainer, borderRadius: borderRadiusLG, minHeight: 'calc(100vh - 64px - 32px)' }}>
            {renderContent()}
          </div>

        </Content>
      </Layout>
      <PersonalCenter
        open={isPersonalCenterOpen}
        onClose={() => setPersonalCenterOpen(false)}  // 关闭弹窗
      />
    </Layout>
  );
}

function ApiDocumentationPage() {
  return (
    <div
      style={{
        minHeight: '100vh',
        padding: 24,
        background: '#f5f7fb',
      }}
    >
      <ApiDocumentCenter />
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/error" element={<ErrorPage />} />
      <Route
        path="/docs/scam-report-enterprise/api/*"
        element={
          <AuthRoute>
            <ApiDocumentationPage />
          </AuthRoute>
        }
      />
      <Route
        path="/docs/scam-report/api/*"
        element={
          <AuthRoute>
            <ApiDocumentationPage />
          </AuthRoute>
        }
      />
      <Route path="/*" element={
        <AuthRoute>
          <MainLayout />
        </AuthRoute>
      } />
    </Routes>
  );
}
export default App;
